import * as ko from "knockout";
import {IMessageData, IObjectData, ISyncField, TSyncfield} from '../../../index';
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import {
    createErrorMessage,
    getObjectDefinition,
    getObjectDisplayName,
    getSourceTranslation,
    getSyncField,
    getSyncFieldValue,
    IObjectDefinition
} from "../../../util/misc";
import {IDialogOptions} from "../../dialog/index";
import {nsSyncFields} from "../../../req/nsSyncFields";
import {IRequestObject} from "../../../req/index";
import {REQUEST_MESSAGE, UPDATING_MESSAGE} from "../../../constants/Language";
import {ButtonManager} from "../../../util/ButtonManager";
import {getCancelButton, getContinueButton} from "../../../constants/RouteDefinitions";
import PromiseRequest from "../../../req/PromiseRequest";
import {convertFromServerString, convertToServerString, getDateTime} from "../../../util/datetime";
import {global} from "../../../appContext";
import manual_syncfield_editHtml from './manual.syncfield.edit.html?raw';

export let template = manual_syncfield_editHtml;

interface IParams {
    object: IObjectData
    syncfieldname: TSyncfield
    dialog: IDialogOptions
    inputAttributes?: any
}

enum Step {
    Init,
    Form,
    Process,
    Success,
    Error,
}

export class viewModel {
    private readonly Step = Step;
    private step: KnockoutObservable<Step | null> = ko.observable(null);
    private messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    private isReady: KnockoutObservable<boolean> = ko.observable(true);
    private kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private syncfield!: ISyncField;
    private value: KnockoutObservable<string | null> = ko.observable(null);
    private object: IObjectData;
    private objDef: IObjectDefinition;
    private objName: string;
    private req_getsyncfields?: IRequestObject
    private req_updatemanualobjectfield: PromiseRequest = new PromiseRequest('updatemanualobjectfield')
    private readonly title!: KnockoutObservable<string>
    private readonly pageButtons: ButtonManager = new ButtonManager()

    constructor(private params: IParams) {
        this.kosub.subscribe(this.step, (step: Step | null) => {
            if (step !== null) {
                this.onStepChange(step)
            }
        })
        if (ko.isObservable(params.dialog.title)) {
            this.title = params.dialog.title
        } else {
            this.title = ko.observable(String(params.dialog.title ?? ''))
        }
        this.object = params.object;
        this.syncfield = getSyncField(params.syncfieldname, this.object.syncfields)!;
        this.objDef = getObjectDefinition(this.object.type.id)!;
        this.objName = getSyncFieldValue(this.objDef.syncfieldDisplayName ?? [], this.object.syncfields) ?? '';
        this.step(Step.Init);

        this.pageButtons.add(getCancelButton(() => params.dialog.doClose?.(), {
            enabled: () => this.step() !== Step.Process,
            icon: () => {
                const step = this.step();
                return step !== null && [Step.Success, Step.Error].includes(step) ? 'fa-times' : 'fa-ban';
            },
            label: () => {
                const step = this.step();
                return step !== null && [Step.Success, Step.Error].includes(step) ? 'close' : 'cancel';
            },
            validator: () => {
                const step = this.step();
                return step === null || ![Step.Process].includes(step);
            },
        }))

        this.pageButtons.add(getContinueButton({
            onClick: () => this.step(Step.Process),
            validator: () => {
                const step = this.step();
                return step !== null && [Step.Form].includes(step);
            },
        }))

    }

    private onStepChange(step: Step) {
        switch (step) {
            case Step.Init: {
                Promise.resolve()
                    .then(() => {
                        if (!this.syncfield) {
                            this.isReady(false)
                            this.messageData({message: REQUEST_MESSAGE})
                            this.req_getsyncfields = nsSyncFields.getSyncFields(this.object.type.id);
                            return this.req_getsyncfields!.request
                                .then((data: ISyncField[]) => {
                                    this.syncfield = getSyncField(this.params.syncfieldname, data)!
                                })
                        }
                    })
                    .then(() => {
                        this.title(`${this.title()} [${getSourceTranslation(this.syncfield)}]`)
                        this.value(this.syncfield.value)
                        this.step(Step.Form)
                        this.messageData(null)
                        this.isReady(true)
                    })
                    .catch((reason: string) => {
                        this.messageData(createErrorMessage(String(reason)))
                        this.step(Step.Error)
                    })
                break;
            }
            case Step.Form: {
                break;
            }

            case Step.Process: {
                this.isReady(false)
                this.messageData({message: UPDATING_MESSAGE})
                const rawValue = this.value();
                const value = rawValue ? convertToServerString(convertFromServerString(rawValue)) : '';
                this.req_updatemanualobjectfield
                    .request({
                        id: this.object.id,
                        value: value,
                        fieldid: this.syncfield.syncfieldid,
                    })
                    .then(() => {
                        this.messageData(null)
                        global.cacheManager.removeCache('searchobject')
                        global.cacheManager.removeCache('getobject')
                        this.step(Step.Success)
                    })
                    .catch((reason: string) => {
                        this.step(Step.Error)
                        this.messageData(createErrorMessage(String(reason)))
                    })
                break
            }

            case Step.Success:
            case Step.Error: {
                this.isReady(true)
            }
        }
    }

    private getSourceTranslation(): string {
        return getSourceTranslation(this.syncfield)
    }

    private getDisplayName(): string {
        return getObjectDisplayName(this.object) ?? ''
    }

    private getDisplayValue(): string {
        const rawValue = this.value();
        if (this.syncfield.fieldtype == 'DateTime') {
            return rawValue ? getDateTime(rawValue, true) : '';
        }
        return rawValue ?? '';
    }

    dispose() {
        this.kosub.unsuscribeAll();
        this.req_getsyncfields?.promise.abort()
        this.req_updatemanualobjectfield.abort()
    }
}
